import academicYearApi from '@/services/academic_year_api';
import classApi from '@/services/class_api';
import courseApi from '@/services/course_api';
import examApi from '@/services/exam_api';
import feeManagementApi from '@/services/fee_management_api';
import { gradeFeePricingApi } from '@/services/grade_fee_pricing_api';
import reportPeriodApi from '@/services/report_period_api';
import roomApi from '@/services/room_api';
import shiftApi from '@/services/shift_api';
import studentApi from '@/services/student_api';
import subjectApi from '@/services/subject_api';
import teacherApi from '@/services/teacher_api';
import transitionApi from '@/services/academic_year_transition_api';
import { getCriteria } from '@/services/promotion_api';
import parentApi from '@/services/parent_api';
import userApi from '@/services/user_api';
import letterGradeApi from '@/services/letter_grade_api';
import { listReportCalculationPolicies } from '@/services/report_calculation_policy_api';
import quizApi from '@/services/quiz_api';
import { transportApi } from '@/services/transport_api';
import { feeScheduleApi } from '@/services/fee_schedule_api';
import templateApi from '@/services/template_api';
import { facultyApi } from '@/services/faculty_api';
import { departmentApi } from '@/services/department_api';
import { programApi } from '@/services/program_api';
import { shortCourseApi } from '@/services/short_course_api';
import { courseBatchApi } from '@/services/course_batch_api';
import { certificateApi } from '@/services/certificate_api';
import { getTotalRecords } from '@/lib/pagination-utils';

// "Is this step done?" checks for guide steps that must create something before the
// user may go on (WalkStep.check). Each asks the API for at least one record.

// List endpoints answer { data: [...] }, { data: { items | ... } } or a plain array.
const listOf = (res: unknown): unknown[] => {
  const r = res as { data?: { data?: unknown } } | unknown[] | undefined;
  const d = Array.isArray(r) ? r : (r?.data?.data ?? r?.data);
  if (Array.isArray(d)) return d;
  const obj = d as Record<string, unknown> | undefined;
  for (const key of ['items', 'data', 'records', 'rows', 'criteria']) {
    if (Array.isArray(obj?.[key])) return obj[key] as unknown[];
  }
  return [];
};
const hasRows = (res: unknown) => listOf(res).length > 0;

const statuses = (res: unknown) => listOf(res).map((x) => (x as { status?: string }).status ?? '');
// a year transition that got at least this far
const transitionReached = async (ok: string[]) =>
  statuses(await transitionApi.getAllTransitions(1, 20)).some((st) => ok.includes(st));

const activeYearId = async (): Promise<number | undefined> => {
  const res = await academicYearApi.getActiveAcademicYear();
  const year = res?.data?.data;
  return (Array.isArray(year) ? year[0]?.id : year?.id) ?? undefined;
};

export const STEP_CHECKS: Record<string, () => Promise<boolean>> = {
  academicYear: async () => hasRows(await academicYearApi.getAcademicYears([])),
  // the setup wizard ends with the year active (first year) or pending (another is active)
  yearReady: async () =>
    listOf(await academicYearApi.getAcademicYears([])).some((y) => ['active', 'pending'].includes((y as { status?: string }).status ?? '')),
  reportPeriods: async () => hasRows(await reportPeriodApi.getAllReportPeriods(true, 1, 1, undefined, await activeYearId())),
  shift: async () => hasRows(await shiftApi.getAllShifts(false)),
  class: async () => hasRows(await classApi.getAllClasses(true, 1, 1)),
  subject: async () => hasRows(await subjectApi.getAllSubjects(1, 1, undefined, true, '')),
  course: async () => hasRows(await courseApi.getAllCourses(true, undefined, undefined, undefined, 1, 1)),
  room: async () => hasRows(await roomApi.getAllRooms(true, 1, 1)),
  teacher: async () => hasRows(await teacherApi.getAllTeachers(true, '', 1, 1)),
  student: async () => hasRows(await studentApi.getStudentBySchoolId(true, 1, 1)),
  feeType: async () => (await feeManagementApi.getFeeTypes(true)).length > 0,
  gradePricing: async () => (await gradeFeePricingApi.list({})).length > 0,
  generalExam: async () => hasRows(await examApi.getAllGlobalExam(true, 1, 1)),
  // next academic year
  nextYear: async () => statuses(await academicYearApi.getAcademicYears([])).some((st) => ['draft', 'pending'].includes(st)),
  promotionCriteria: async () => hasRows(await getCriteria()),
  transitionStarted: async () => hasRows(await transitionApi.getAllTransitions(1, 1)),
  transitionPrepared: () => transitionReached(['ready_for_review', 'approved', 'executing', 'completed']),
  transitionApproved: () => transitionReached(['approved', 'executing', 'completed']),
  transitionCompleted: () => transitionReached(['completed']),
  // people
  parent: async () => hasRows(await parentApi.getParentBySchoolId(true, 1, 1)),
  staff: async () => hasRows(await userApi.getStaffUsers(true)),
  // grading
  letterGrade: async () => hasRows(await letterGradeApi.getAllLetterGrades(true, 1, 1)),
  policySaved: async () => hasRows(await listReportCalculationPolicies({ limit: 1 })),
  policyActive: async () => hasRows(await listReportCalculationPolicies({ status: 'active', limit: 1 })),
  quiz: async () => hasRows(await quizApi.getAllQuizzes(1, 1)),
  // transport
  driver: async () => (await transportApi.drivers()).length > 0,
  route: async () => (await transportApi.routes()).length > 0,
  transportStudent: async () => (await transportApi.transportStudents()).length > 0,
  // money and school
  feeSchedule: async () => (await feeScheduleApi.getSchedules()).length > 0,
  documentTemplate: async () => hasRows(await templateApi.getTemplate()),
  // university and training
  faculty: async () => hasRows(await facultyApi.getAllFaculties({ page: 1, per_page: 1 })),
  department: async () => hasRows(await departmentApi.getAllDepartments({ page: 1, per_page: 1 })),
  program: async () => hasRows(await programApi.getAllPrograms({ page: 1, per_page: 1 })),
  shortCourse: async () => hasRows(await shortCourseApi.getAllShortCourses({ page: 1, per_page: 1 })),
  batch: async () => hasRows(await courseBatchApi.getAllBatches({ page: 1, per_page: 1 })),
  certificateTemplate: async () => hasRows(await certificateApi.getAllTemplates({ page: 1, per_page: 1 })),
};

// Record counts for steps that must add a NEW record (WalkStep.countUp): the step is done
// once the count is higher than when the step opened.
export const STEP_COUNTS: Record<string, () => Promise<number>> = {
  teachers: async () => getTotalRecords(await teacherApi.getAllTeachers(true, '', 1, 1)),
  students: async () => getTotalRecords(await studentApi.getStudentBySchoolId(true, 1, 1)),
};

// null = unknown (failed request or no such count): the caller must not block on it.
export const runStepCount = async (name: string): Promise<number | null> => {
  try {
    return (await STEP_COUNTS[name]?.()) ?? null;
  } catch {
    return null;
  }
};

// true = done, false = not yet, null = unknown (failed request or no such check).
// The caller must not block on null, but must not claim the step is done either.
export const runStepCheck = async (name: string): Promise<boolean | null> => {
  const check = STEP_CHECKS[name];
  if (!check) return null;
  try {
    return await check();
  } catch {
    return null;
  }
};
